import { useEffect, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  Grid,
  Users,
  TrendingUp,
  Search,
  Layers,
  BarChart,
  CheckCircle,
  ArrowRight,
} from 'lucide-react'
import { LandingHeader } from '../components/landing/LandingHeader'
import { LandingFooter } from '../components/landing/LandingFooter'

gsap.registerPlugin(ScrollTrigger)

export const Route = createFileRoute('/wawasan-product')({
  component: WawasanProductPage,
})

const features = [
  {
    Icon: Grid,
    title: '9-Box Grid Visualization',
    description: "Automatically position every employee on the 9-box talent grid based on their performance scores and competency levels. Instantly see your talent distribution.",
  },
  {
    Icon: Users,
    title: 'Talent Mapping',
    description: "Get a bird's-eye view of your entire workforce — who's high potential, who needs support, and how talent is distributed across divisions and roles.",
  },
  {
    Icon: TrendingUp,
    title: 'AI-Powered Talent Reports',
    description: 'Generate strategic workforce reports with one click. Summarize competency strengths, flag development gaps, and surface succession candidates automatically.',
  },
  {
    Icon: Search,
    title: 'Competency Gap Analysis',
    description: "Compare every employee's current competency profile against their position requirements. See exactly where the gaps are and prioritize development.",
  },
  {
    Icon: Layers,
    title: 'Individual Development Plans',
    description: 'Track IDP goals, milestones, and progress for each employee. Link development actions directly to competency gaps identified in assessments.',
  },
  {
    Icon: BarChart,
    title: 'Strategic Workforce Reports',
    description: 'Export board-ready talent intelligence with trend analysis — understand where your organization is growing and where it needs investment.',
  },
]

const benefits = [
  'Make talent decisions backed by data, not intuition',
  'Automatically map your entire team on the 9-Box grid',
  'Surface competency gaps and development priorities instantly',
  'Align talent investment with business strategy',
]

// ─── Hero Mockup ───────────────────────────────────────────────────────────────

function WawasanHeroMockup() {
  // 9-box grid: row 0 = high potential, col 2 = high performance
  const dots: Array<{ row: number; col: number; initials: string; color: string }> = [
    { row: 0, col: 2, initials: 'SC', color: '#359d9e' },
    { row: 0, col: 1, initials: 'HK', color: '#27777a' },
    { row: 1, col: 2, initials: 'RP', color: '#359d9e' },
    { row: 1, col: 0, initials: 'AI', color: '#b3b7b7' },
    { row: 2, col: 1, initials: 'BW', color: '#b3b7b7' },
  ]
  const cellColor = (r: number, c: number) => {
    if (r === 0 && c === 2) return '#359d9e'
    if ((r === 0 && c === 1) || (r === 1 && c === 2)) return '#d5f2ef'
    if (r === 2 && c === 0) return '#f2f3f3'
    return '#f8fffe'
  }

  return (
    <div className="relative w-full h-full select-none">
      {/* Main panel */}
      <div className="absolute rounded-2xl overflow-hidden shadow-[0px_8px_40px_0px_rgba(53,157,158,0.18)] bg-white border border-[rgba(53,157,158,0.12)]"
        style={{ top: '8%', left: '0%', right: '8%', bottom: '6%' }}>
        <div className="flex items-center gap-1.5 px-3 py-2 bg-[#f8fffe] border-b border-[rgba(53,157,158,0.1)]">
          <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <div className="flex-1 mx-3 h-4 rounded bg-[#e6e7e7] text-[9px] text-[#b3b7b7] flex items-center px-2">pelajari.ai/wawasan</div>
        </div>
        <div className="p-4 flex gap-4">
          {/* 9-box grid */}
          <div className="flex-shrink-0">
            <p className="text-[8px] font-bold text-[#b3b7b7] uppercase tracking-wider mb-2">9-Box Grid</p>
            <div className="grid grid-cols-3 gap-1" style={{ width: 108 }}>
              {[0, 1, 2].map((r) =>
                [0, 1, 2].map((c) => {
                  const dot = dots.find((d) => d.row === r && d.col === c)
                  return (
                    <div key={`${r}-${c}`} className="h-8 rounded flex items-center justify-center"
                      style={{ backgroundColor: cellColor(r, c) }}>
                      {dot && (
                        <div className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center text-[7px] font-bold text-white"
                          style={{ backgroundColor: dot.color }}>
                          {dot.initials}
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
            <div className="flex justify-between mt-1" style={{ width: 108 }}>
              <span className="text-[7px] text-[#b3b7b7]">Low Perf</span>
              <span className="text-[7px] text-[#b3b7b7]">High Perf</span>
            </div>
          </div>
          {/* Right cards */}
          <div className="flex-1 flex flex-col gap-2 min-w-0">
            <p className="text-[8px] font-bold text-[#b3b7b7] uppercase tracking-wider">AI Insights</p>
            <div className="bg-[#f2fbfa] border border-[#d5f2ef] rounded-lg p-2">
              <div className="flex items-center gap-1.5 mb-1">
                <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 flex-shrink-0" fill="#359d9e">
                  <path d="M5 0L6.2 3.8H10L7 6.2L8.1 10L5 7.6L1.9 10L3 6.2L0 3.8H3.8L5 0Z"/>
                </svg>
                <p className="text-[9px] font-semibold text-[#27777a]">Sarah Chen — Star Player</p>
              </div>
              <p className="text-[8px] text-[#1a2727]">High potential · Exceeds expectations · Succession candidate</p>
            </div>
            <div className="border border-[rgba(53,157,158,0.12)] rounded-lg p-2">
              <p className="text-[8px] font-semibold text-[#1a2727] mb-1">IDP: Leadership Skills</p>
              <div className="h-1.5 bg-[#e6e7e7] rounded-full overflow-hidden">
                <div className="h-1.5 bg-[#359d9e] rounded-full" style={{ width: '65%' }} />
              </div>
              <p className="text-[7px] text-[#b3b7b7] mt-1">65% complete</p>
            </div>
            <div className="border border-[rgba(53,157,158,0.12)] rounded-lg p-2 flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded bg-[#fff0e9] flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 8 8" className="w-2 h-2" fill="none">
                  <path d="M4 1V4M4 5.5V6" stroke="#fc8213" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <p className="text-[8px] font-semibold text-[#1a2727]">Gap: Communication</p>
                <p className="text-[7px] text-[#b3b7b7]">Below position requirement</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Floating card */}
      <div className="wawasan-float-card absolute bg-white rounded-xl border border-[rgba(53,157,158,0.15)] shadow-[0px_4px_20px_0px_rgba(53,157,158,0.2)] px-3 py-2.5 flex items-center gap-2.5"
        style={{ bottom: '0%', right: '0%', maxWidth: '55%' }}>
        <div className="w-6 h-6 rounded-full bg-[#f2fbfa] flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 12 12" className="w-3.5 h-3.5" fill="none">
            <circle cx="4.5" cy="3.5" r="2" stroke="#359d9e" strokeWidth="1.1"/>
            <path d="M1 10c0-1.93 1.57-3.5 3.5-3.5S8 8.07 8 10" stroke="#359d9e" strokeWidth="1.1" strokeLinecap="round"/>
            <path d="M9 5l1.5 1.5L9 8" stroke="#fc8213" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div>
          <p className="text-[9px] font-bold text-[#1a2727]">3 Succession Candidates</p>
          <p className="text-[8px] text-[#b3b7b7]">Ready for next level</p>
        </div>
      </div>
    </div>
  )
}

function WawasanProductPage() {
  const pageRef = useRef<HTMLDivElement>(null)

  // ── Hero refs
  const heroBadgeRef = useRef<HTMLSpanElement>(null)
  const heroHeadingRef = useRef<HTMLHeadingElement>(null)
  const heroSubRef = useRef<HTMLParagraphElement>(null)
  const heroCtaRef = useRef<HTMLDivElement>(null)
  const heroMockupRef = useRef<HTMLDivElement>(null)

  // ── Features refs
  const featHeadingRef = useRef<HTMLDivElement>(null)
  const featCardRefs = useRef<(HTMLDivElement | null)[]>([])

  // ── Benefits refs
  const benefitsLeftRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // ── Hero entrance timeline
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })
      gsap.set([heroBadgeRef.current, heroHeadingRef.current, heroSubRef.current, heroCtaRef.current], { opacity: 0, y: 28 })
      gsap.set(heroMockupRef.current, { opacity: 0, x: 48 })

      tl.to(heroBadgeRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'back.out(1.7)' })
        .to(heroHeadingRef.current, { opacity: 1, y: 0, duration: 0.7 }, '-=0.3')
        .to(heroSubRef.current, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4')
        .to(heroCtaRef.current, { opacity: 1, y: 0, duration: 0.55 }, '-=0.35')
        .to(heroMockupRef.current, { opacity: 1, x: 0, duration: 0.85, ease: 'power2.out' }, 0.15)

      // Mockup idle float
      tl.add(() => {
        gsap.to(heroMockupRef.current, { y: -10, duration: 3.2, ease: 'sine.inOut', yoyo: true, repeat: -1 })
        gsap.to('.wawasan-float-card', { y: -7, duration: 2.9, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 0.7 })
      })

      // ── Features section
      gsap.set(featHeadingRef.current, { opacity: 0, y: 24 })
      ScrollTrigger.create({
        trigger: featHeadingRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(featHeadingRef.current, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }),
      })

      gsap.set(featCardRefs.current.filter(Boolean), { opacity: 0, y: 28 })
      ScrollTrigger.create({
        trigger: featCardRefs.current[0],
        start: 'top 85%',
        once: true,
        onEnter: () => gsap.to(featCardRefs.current.filter(Boolean), {
          opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.08,
        }),
      })

      // ── Benefits + CTA section
      gsap.set(benefitsLeftRef.current, { opacity: 0, y: 28 })
      ScrollTrigger.create({
        trigger: benefitsLeftRef.current,
        start: 'top 82%',
        once: true,
        onEnter: () => gsap.to(benefitsLeftRef.current, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }),
      })

      const statItems = statsRef.current?.querySelectorAll('.stat-item')
      if (statItems?.length) {
        gsap.set(statItems, { opacity: 0, scale: 0.75 })
        ScrollTrigger.create({
          trigger: statsRef.current,
          start: 'top 85%',
          once: true,
          onEnter: () => gsap.to(statItems, {
            opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.7)', stagger: 0.1,
          }),
        })
      }
    }, pageRef)

    return () => ctx.revert()
  }, [])

  return (
    <div ref={pageRef} className="min-h-screen bg-white overflow-x-hidden font-sans">
      <LandingHeader />

      {/* Hero — 2-col split */}
      <section className="relative bg-gradient-to-br from-[#f2fbfa] to-white py-20 px-6 md:px-10 lg:px-[120px] overflow-hidden">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-center lg:gap-16">
            <div className="flex-1 max-w-[560px]">
              <span ref={heroBadgeRef} className="inline-block bg-[#f2fbfa] border border-[#359d9e] text-[#359d9e] text-sm font-medium px-4 py-2 rounded-lg mb-6">
                Wawasan — Talent Intelligence
              </span>
              <h1 ref={heroHeadingRef} className="text-[36px] font-semibold leading-[44px] tracking-[-1px] text-[#000f0f] sm:text-[48px] sm:leading-[58px] lg:text-[56px] lg:leading-[68px] lg:tracking-[-1.5px] mb-6">
                Unlock Talent Intelligence with{' '}
                <span className="text-[#359d9e]">9-Box AI</span>
              </h1>
              <p ref={heroSubRef} className="text-base leading-relaxed text-[#1a2727] mb-8">
                Go beyond spreadsheets. Wawasan automatically maps your entire workforce onto the 9-box grid, surfaces competency gaps with AI-powered reports, and delivers the strategic insights that drive smarter talent decisions.
              </p>
              <div ref={heroCtaRef} className="flex flex-col sm:flex-row gap-4">
                <a href="/login" className="inline-flex items-center justify-center gap-2 bg-[#359d9e] text-white hover:bg-[#27777a] rounded px-6 py-3 font-medium transition-colors">
                  Explore Talent Intelligence
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#features" className="inline-flex items-center justify-center gap-2 border border-[#359d9e] text-[#359d9e] hover:bg-[#f2fbfa] rounded px-6 py-3 font-medium transition-colors">
                  See Features
                </a>
              </div>
            </div>
            <div ref={heroMockupRef} className="w-full max-w-[440px] h-[340px] sm:h-[380px] lg:h-[400px] flex-shrink-0">
              <WawasanHeroMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-6 md:px-10 lg:px-[120px] max-w-screen-xl mx-auto">
        <div ref={featHeadingRef} className="text-center mb-14">
          <h2 className="text-[32px] font-semibold leading-[40px] tracking-[-0.5px] text-[#000f0f] sm:text-[40px] sm:leading-[48px] mb-4">
            AI-powered talent insights at every level
          </h2>
          <p className="text-base text-[#1a2727] max-w-[600px] mx-auto">
            Wawasan turns raw performance data into strategic workforce intelligence — in minutes, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, i) => {
            const { Icon } = feature
            return (
              <div
                key={feature.title}
                ref={(el) => { featCardRefs.current[i] = el }}
                className="bg-white border border-[#d5f2ef] rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-[#f2fbfa] rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#359d9e]" />
                </div>
                <h3 className="text-lg font-semibold text-[#000f0f] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#1a2727] leading-relaxed">{feature.description}</p>
              </div>
            )
          })}
        </div>
      </section>

      {/* Benefits + CTA */}
      <section className="bg-[#359d9e] py-16 px-6 md:px-10 lg:px-[120px]">
        <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <div ref={benefitsLeftRef} className="flex-1 text-center lg:text-left">
            <h2 className="text-[30px] font-semibold leading-[38px] text-white mb-6 lg:text-[36px] lg:leading-[45px]">
              Why HR leaders choose Wawasan
            </h2>
            <div className="flex flex-col gap-4 mb-8">
              {benefits.map((b) => (
                <div key={b} className="flex items-start gap-3 justify-center lg:justify-start">
                  <CheckCircle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-base text-white">{b}</span>
                </div>
              ))}
            </div>
            <a
              href="/login"
              className="inline-flex items-center gap-2 bg-white text-[#359d9e] hover:bg-white/90 rounded px-6 py-3 font-medium transition-colors"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div ref={statsRef} className="flex-1 bg-white/10 rounded-xl p-8 max-w-[460px] w-full">
            <div className="grid grid-cols-2 gap-6 text-center">
              {[
                { value: '9-Box', label: 'Grid Auto-Placement' },
                { value: 'AI', label: 'Gap Detection' },
                { value: '<1hr', label: 'Full Workforce Map' },
                { value: '360°', label: 'Data Sources' },
              ].map((stat) => (
                <div key={stat.label} className="stat-item flex flex-col gap-1">
                  <span className="text-[32px] font-semibold text-white">{stat.value}</span>
                  <span className="text-sm text-white/80">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  )
}
